export enum UserRole {
  GlobalAdmin = 'GlobalAdmin',
  CompanyAdmin = 'CompanyAdmin',
  CompanyProjectManager = 'CompanyProjectManager',
  /** Same application permissions as Company Project Manager and Company Operator combined; requires `companyID`. */
  EngineeringProjectManager = 'EngineeringProjectManager',
  CompanyOperator = 'CompanyOperator',
  QualityControl = 'QualityControl',
  Viewer = 'Viewer',
}

/**
 * Whether this role may open a project without a direct user–project assignment when the user's
 * company is assigned to the project (GlobalAdmin may access any project).
 */
export function mayAccessProjectViaCompanyScope(role: UserRole | null | undefined): boolean {
  return (
    role === UserRole.GlobalAdmin ||
    role === UserRole.CompanyAdmin ||
    role === UserRole.CompanyProjectManager ||
    role === UserRole.EngineeringProjectManager
  );
}

/**
 * pile load test log reports (`POST …/pull-testing/reports/load-test-log`).
 * {@link UserRole.GlobalAdmin}, {@link UserRole.CompanyAdmin}, {@link UserRole.CompanyProjectManager}, and
 * {@link UserRole.EngineeringProjectManager}.
 */
export function mayRequestPullTestingLoadTestLogReport(role: UserRole | null | undefined): boolean {
  return (
    role === UserRole.GlobalAdmin ||
    role === UserRole.CompanyAdmin ||
    role === UserRole.CompanyProjectManager ||
    role === UserRole.EngineeringProjectManager
  );
}

/** PM roles that may acknowledge or restore offline sync collisions from the events log. */
export function mayResolvePullTestingOfflineSyncCollisions(
  role: UserRole | null | undefined
): boolean {
  return mayRequestPullTestingLoadTestLogReport(role);
}

/**
 * Whether this role may use pull-testing operator actions (install / test flows and dashboard CTAs).
 * {@link UserRole.Viewer} is read-only for pull testing.
 */
export function mayUsePullTestingDashboardActions(role: UserRole | null | undefined): boolean {
  return role != null && role !== UserRole.Viewer;
}

/**
 * Whether a user belongs in project team rosters (sidebar, manage team, directory lists).
 * Global and company admins may have project assignments for last-view tracking but are excluded from
 * “who is on the project” UI — they already have broad access within their scope.
 */
export function isProjectRosterMember(role: UserRole | null | undefined): boolean {
  return role != null && role !== UserRole.GlobalAdmin && role !== UserRole.CompanyAdmin;
}

/** Omits {@link UserRole.GlobalAdmin} and {@link UserRole.CompanyAdmin} from roster-style user lists. */
export function filterProjectRosterUsers(users: readonly User[]): User[] {
  return users.filter((u) => isProjectRosterMember(u.role));
}

export interface User {
  userID: string;
  email: string;
  name: string;
  phoneNumber?: string;
  companyID: string;
  /** When the API includes it (e.g. enriched user lists), prefer this for display over raw `companyID`. */
  companyName?: string;
  role: UserRole;
  status: 'CONFIRMED' | 'UNCONFIRMED' | 'FORCE_CHANGE_PASSWORD';
  enabled?: boolean;
  eulaAccepted?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Optional opaque avatar background when showing initials (`#RRGGBB`).
   * May originate from Cognito custom attributes once backend maps them into API responses.
   */
  profileAvatarHexColor?: string | null;
  /**
   * Optional profile photo URL — typically HTTPS, often a short-lived presigned S3 URL from your API.
   * When absent or load fails, the UI falls back to colored initials.
   */
  profilePictureUrl?: string | null;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  phoneNumber?: string;
  name?: string;
  companyID: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  email?: string;
  phoneNumber?: string;
  name?: string;
  companyID?: string;
  role?: UserRole;
  acceptEULA?: boolean;
  profileAvatarHexColor?: string | null;
  profilePictureUrl?: string | null;
}

export interface ProjectAssignment {
  projectID: string;
  role: string;
  table: string;
}

export interface StaleUser {
  userID: string;
  email: string;
  name: string;
  companyID: string;
  role: UserRole;
  status: 'CONFIRMED' | 'UNCONFIRMED' | 'FORCE_CHANGE_PASSWORD';
  createdAt: string;
  lastModifiedAt: string;
  daysSinceInvite: number;
  projectAssignments?: ProjectAssignment[];
}

export interface StaleUserDeletionResult {
  userID: string;
  email: string;
  daysSinceInvite: number;
  deletedAssignments: ProjectAssignment[];
  errors: string[];
}

/** Payload for requesting a new user (reviewed by admins). POST /users/requests */
export interface UserRequestPayload {
  email: string;
  fullName: string;
  phoneNumber?: string;
  role: UserRole;
  companyID?: string; // Required for all roles except GlobalAdmin, QualityControl, Viewer
}

/** Response from creating a user request */
export interface UserRequestResponse {
  message: string;
  requestId: string;
  submittedAt: string;
}

/** Single item in list user requests response */
export interface UserRequestSummary {
  requestId: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  role: UserRole;
  companyID?: string;
  requestedBy?: string;
  requestedByName?: string;
  requestedByEmail?: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'denied';
}

/** Response from list user requests */
export interface UserRequestListResponse {
  requests: UserRequestSummary[];
  pagination: {
    hasMore: boolean;
    nextToken: string;
  };
}

/** Payload for updating a user request (all fields optional) */
export interface UserRequestUpdatePayload {
  email?: string;
  fullName?: string;
  phoneNumber?: string;
  role?: UserRole;
  companyID?: string;
}

/** Response from approving a user request */
export interface ApproveUserRequestResponse {
  message: string;
  requestId: string;
  user?: {
    userID: string;
    email: string;
    name: string;
    role: UserRole;
    companyID: string;
  };
  approvedAt: string;
} 